import React, { FC, RefObject, useEffect, useState } from 'react';
import { useNodePositions } from '../hooks/use-node-positions.hook';
import {
  JSCompletedProgressLine,
  JSCurrentSegmentProgressLine,
  JSErrorProgressLine,
  JSProgressLine,
} from '../styled/progress-lines-js.styled';

interface JSProgressLinesProps {
  stepCount: number;
  activeStepIndex: number;
  progressPercentage: number;
  hasError: boolean;
  containerRef: RefObject<HTMLElement | null>;
}

export const JSProgressLines: FC<JSProgressLinesProps> = ({
  stepCount,
  activeStepIndex,
  progressPercentage,
  hasError,
  containerRef,
}) => {
  // Track the previous activeStepIndex to detect step changes
  const [prevActiveStepIndex, setPrevActiveStepIndex] =
    useState(activeStepIndex);
  const [shouldUseKeyframeAnimation, setShouldUseKeyframeAnimation] =
    useState(true);

  // Detect step changes (either initial mount or step switching)
  useEffect(() => {
    if (prevActiveStepIndex !== activeStepIndex) {
      // Active step changed - use keyframe animation (start from 0)
      setShouldUseKeyframeAnimation(true);
      setPrevActiveStepIndex(activeStepIndex);

      // After animation starts, switch to transition mode for PNL updates
      const timer = setTimeout(() => {
        setShouldUseKeyframeAnimation(false);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [activeStepIndex, prevActiveStepIndex]);

  // For real-time PNL changes (when activeStepIndex stays the same)
  useEffect(() => {
    if (
      prevActiveStepIndex === activeStepIndex &&
      !shouldUseKeyframeAnimation
    ) {
      // This is a PNL change, not a step change - use transition
      // (shouldUseKeyframeAnimation is already false)
    }
  }, [
    progressPercentage,
    activeStepIndex,
    prevActiveStepIndex,
    shouldUseKeyframeAnimation,
  ]);

  const { progressLinePositions } = useNodePositions(
    containerRef,
    stepCount,
    activeStepIndex,
    progressPercentage,
  );

  // Don't render anything until we have calculated positions
  if (!progressLinePositions) {
    return null;
  }

  const { backgroundLine, completedLine, currentLine } = progressLinePositions;

  return (
    <>
      {/* Background progress line */}
      <JSProgressLine
        left={backgroundLine.left}
        width={backgroundLine.width}
        id="js-progress-line-background"
      />

      {/* Completed progress line (only if there are completed segments) */}
      {activeStepIndex > 0 && completedLine.width > 0 && (
        <JSCompletedProgressLine
          key={`completed-${activeStepIndex}`}
          left={completedLine.left}
          width={completedLine.width}
          id="js-progress-line-completed"
        />
      )}

      {/* Current segment progress line (only if there's progress to show) */}
      {activeStepIndex >= 0 &&
        currentLine.width > 0 &&
        (hasError ? (
          <JSErrorProgressLine
            key={
              shouldUseKeyframeAnimation
                ? `keyframe-error-${activeStepIndex}`
                : `transition-error-${activeStepIndex}`
            }
            left={currentLine.left}
            width={currentLine.width}
            isInitialMount={shouldUseKeyframeAnimation}
            id="js-progress-line-error"
          />
        ) : (
          <JSCurrentSegmentProgressLine
            key={
              shouldUseKeyframeAnimation
                ? `keyframe-${activeStepIndex}`
                : `transition-${activeStepIndex}`
            }
            left={currentLine.left}
            width={currentLine.width}
            isInitialMount={shouldUseKeyframeAnimation}
            id="js-progress-line-current"
          />
        ))}
    </>
  );
};
